import { _ } from "../../../../core/polyglot/polyglot.js";
import AbstractForecastReport from "./AbstractForecastReport.js";
import accountRowVisibilityFactory from "../../../reports/accountRowVisibilities/accountRowVisibilityFactory.js";
import BalanceSheetChartStrategy from "../../../../accounting/forecastEditor/widgets/ForecastReportViewerWidget/BalanceSheetChartStrategy.js";
import BalanceSheetForecastFiscalYearCardRenderingStrategy from "../../../../accounting/forecastEditor/widgets/ForecastReportViewerWidget/fiscalYearCardStrategy/BalanceSheetForecastFiscalYearCardRenderingStrategy.js";
import ClosingBalanceAccountOperation from "../../../keyFigure/accountOperations/ClosingBalanceAccountOperation.js";
import ManualJournalEntryRuleModel from "../../ruleModels/ManualJournalEntryRuleModel.js";
import Timespan from "../../../period/Timespan.js";

export const adjustedEquityId = "AdjustedEquity";

/**
 * Report specific to Forecast's Balance sheet.
 */
export default class ForecastBalanceSheet extends AbstractForecastReport {
	getTitle() {
		return _("Balance Sheet");
	}

	getSummaryRowTitle() {
		return _("Adjusted Equity");
	}

	isBalanceSheet() {
		return true;
	}

	getOperationType() {
		return ClosingBalanceAccountOperation.getOperationType();
	}

	getChartValues() {
		return this._keyFigureValues.get(adjustedEquityId);
	}

	getChartStrategy() {
		return new BalanceSheetChartStrategy({
			labels: this._getColumnTitles(),
		});
	}

	getChartDataTestName() {
		return "balance-sheet-chart";
	}

	getFiscalYearCardRenderingStrategy() {
		return new BalanceSheetForecastFiscalYearCardRenderingStrategy();
	}

	getValuesForCard({ closingColumnIndex } = {}) {
		const equity = this._keyFigureValues.get(adjustedEquityId);

		const openingColumnIndex = this._getOpeningColumnIndexFor({
			closingColumnIndex,
		});

		if (!equity?.length) return {};

		const getPercentage = (series) => {
			if (openingColumnIndex === closingColumnIndex) {
				return NaN;
			}

			const current = series[openingColumnIndex]?.getValue();
			const closing = series[closingColumnIndex]?.getValue();

			if (!current || !closing) {
				return NaN;
			}

			const ratio = (closing - current) / current;
			return Math.floor(ratio * 100);
		};

		return {
			timespan: this._getCardTimespanFor({ closingColumnIndex }),
			openingEquityValue: equity[openingColumnIndex]?.getValue(),
			closingEquityValue: equity[closingColumnIndex]?.getValue(),
			equityPercentageGrowth: getPercentage(equity),
		};
	}

	rulesRendererFor(forecastCellInfoWidget) {
		return forecastCellInfoWidget.rulesRendererForBalanceSheet();
	}

	getAvailableRuleModelClasses() {
		return [ManualJournalEntryRuleModel];
	}

	_getCardTimespanFor({ closingColumnIndex }) {
		const { openingColumn, closingColumn } =
			this._getOpeningAndClosingColumnsFor({ closingColumnIndex });

		let startPeriod;
		let endPeriod;

		if (!openingColumn || !closingColumn) {
			return null;
		}

		if (
			// no prediction, the whole period is actual data:
			!closingColumn.isPredictedForecastColumn() ||
			// the whole period is predicted:
			openingColumn.isPredictedForecastColumn()
		) {
			startPeriod = openingColumn.getPeriod();
			endPeriod = closingColumn.getPeriod();
		} else {
			// the period is partially actual data and partially predicted:
			startPeriod = this.getPredictedColumns()[0].getPeriod();
			endPeriod = closingColumn.getPeriod();
		}

		return new Timespan({
			start: startPeriod.getStart(),
			end: endPeriod.getEnd(),
		});
	}

	static getDefaultAccountVisibility() {
		return accountRowVisibilityFactory.hideNoDataAccountsVisibility();
	}

	static getType() {
		return "balance-sheet";
	}

	static getSummaryRowKeyFigureId() {
		return adjustedEquityId;
	}

	static getChartKeyFigureIds() {
		return [adjustedEquityId];
	}

	static getCardKeyFigureIds() {
		return [adjustedEquityId];
	}
}
